/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import ContentTranslationUpsert from "components/ContentTranslationUpsert";
import ContentTranslationDelete from "components/ContentTranslationDelete";
import ContentTranslationDropDownOptions from "components/ContentTranslationDropDownOptions";

/*
 * GRAPHS
 */
import ContentTranslationReadQuery from "./__query__/index.contentTranslation.read.query";

/*
 * STYLES
 */
import { buttonStyle, tabStyles } from "./index.style";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _Customers = Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums.map(
    (i) => i.key,
  );
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [selectAll, setSelectAll] = React.useState(false);
  const [tabIndex, setTabIndex] = React.useState(0);
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [selectedIds, setSelectedIds] = React.useState([]);

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;

  const _QueryContentTranslationRead = useQuery(ContentTranslationReadQuery, {
    variables: {
      type: _Customers[tabIndex],
      filter: _appliedFilter,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
    pollInterval: Object.React.App.pollInterval,
  });

  const _isFirstLoadCompleted = React.useRef(false);
  const {
    onOpen: onContentTranslationOpen,
    isOpen: isContentTranslationOpen,
    onClose: onContentTranslationClose,
  } = useDisclosure();
  const {
    isOpen: isContentTranslationUpdateOpen,
    onOpen: onContentTranslationUpdateOpen,
    onClose: onContentTranslationUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isContentTranslationDeleteOpen,
    onOpen: onContentTranslationDeleteOpen,
    onClose: onContentTranslationDeleteClose,
  } = useDisclosure();
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryContentTranslationRead.data) {
      // Update states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [
    isContentTranslationUpdateOpen,
    isContentTranslationOpen,
    isContentTranslationDeleteOpen,
    tabIndex,
    paginationModel.page,
  ]);

  // Event handler.
  const _HandleSelectAll = () => {
    // Check if selectAll is true.
    setSelectAll(!selectAll);

    // Check if selectAll is true.
    setSelectedIds(
      selectAll
        ? []
        : _QueryContentTranslationRead?.data?.ContentTranslationRead?.map(
            (item) => item.id,
          ) || [],
    );
  };
  const _HandleCheckboxChange = (id) => {
    // Check if id is already in selectedIds.
    if (selectedIds.includes(id)) {
      // Remove id from selectedIds.
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      // Add id to selectedIds.
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Data assignment.
  if (
    !_QueryContentTranslationRead.loading &&
    0 < _QueryContentTranslationRead?.data?.ContentTranslationRead?.length
  )
    _isFirstLoadCompleted.current = true;
  if (
    0 === _QueryContentTranslationRead?.data?.ContentTranslationRead?.length ||
    !_.every(
      _.pluck(
        _QueryContentTranslationRead?.data?.ContentTranslationRead,
        "status",
      ),
      (j) => _successFlags.includes(j),
    )
  )
    _isFirstLoadCompleted.current = false;

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIds.includes(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },

      {
        field: "sno",
        headerName: "S No.",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "id",
        headerName: "ID",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "CustomerAccount.displayName",
        headerName: "Customer Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Mcc.countryName",
        headerName: "Country (Mcc)",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "regex",
        headerName: "Original Keyword",
        editable: false,
        sortable: true,
        filterable: true,
        // 'disableColumnMenu': true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "prefix",
        headerName: "Prefix",
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "suffix",
        headerName: "Suffix",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "omitWords",
        headerName: "OmitWords",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "strategy",
        headerName: "Match Type",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        width: 100,
        renderCell: ({ row }) => (
          <ContentTranslationDropDownOptions
            contentTranslationId={row?.id}
            onContentTranslationUpdate={onContentTranslationUpdateOpen}
            onContentTranslationDelete={onContentTranslationDeleteOpen}
            selectedIds={selectedIds}
            contentTranslationIds={[...selectedIds, row?.id]}
          />
        ),
      },
    ],
    [selectAll, selectedIds, tabIndex],
  );

  // Define rows
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryContentTranslationRead?.data?.ContentTranslationRead?.map(
          (item, index) =>
            _successFlags.includes(item.status)
              ? {
                  id: item?.id || String.random(8),
                  sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                  "CustomerAccount.displayName":
                    item?.[
                      "TERMINATION" === _Customers[tabIndex]
                        ? "VendorAccount"
                        : "CustomerAccount"
                    ]?.displayName ?? "-",
                  "Mcc.countryName":
                    item?.Mcc?.mcc >= 0
                      ? `${item?.Mcc?.countryName} (${item?.Mcc?.mcc})`
                      : "-",
                  regex: _.isEmpty(item?.regex) ? "-" : item?.regex,
                  prefix: _.isEmpty(item?.prefix) ? "-" : item?.prefix,
                  suffix: _.isEmpty(item?.suffix) ? "-" : item?.suffix,
                  omitWords: _.isEmpty(item?.omitWords)
                    ? "-"
                    : item?.omitWords?.join(", "),
                  strategy: _.isEmpty(item?.strategy) ? "-" : item?.strategy,
                  createdAt: _.isEmpty(item.createdAt)
                    ? "-"
                    : Moment(item.createdAt).format("YYYY-MM-DD"),
                  updatedAt: _.isEmpty(item?.updatedAt)
                    ? "-"
                    : Moment(item?.updatedAt).format("YYYY-MM-DD"),
                }
              : null,
        ),
      ),
    [
      _QueryContentTranslationRead?.data?.ContentTranslationRead,
      paginationModel.page,
      paginationModel.pageSize,
      tabIndex,
    ],
  );

  // Return component.
  return (
    <>
      <Flex
        className="contentTranslation base"
        minH="78vh"
        maxH="78vh"
        width="100%"
        overflow="hidden"
      >
        {/* inner container */}
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
            minH="auto"
          >
            <Tooltip label="Create new Translation." fontSize="sm">
              <Button
                w="auto"
                onClick={onContentTranslationOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New Content Translation
              </Button>
            </Tooltip>
          </Flex>

          {/* Tab and Table section */}
          <Tabs
            variant="enclosed"
            onChange={(r) => setTabIndex(r)}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            bg="white"
            isFitted
            overflow="hidden"
            minH={0}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : void 0}
              flexShrink={0}
              minH="auto"
            >
              {_Customers.map((u) => (
                <Tab
                  color="white"
                  textDecoration={"line-through"}
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                  p="1rem"
                  key={String.random(8)}
                >
                  {u}
                </Tab>
              ))}
            </TabList>

            <Flex
              flex={1}
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
              overflow="hidden"
              minH={0}
              width="100%"
            >
              {/* MUI DataGrid */}
              <DataGrid
                key={`${paginationModel.page}-${paginationModel.pageSize}-${tabIndex}`}
                rows={rows}
                columns={columns}
                rowHeight={58}
                rowCount={
                  _.pluck(
                    _QueryContentTranslationRead?.data?.ContentTranslationRead,
                    "_totalCount",
                  )?.[0] || 0
                }
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={_setFilterDebounced}
                pageSizeOptions={[10, 30, 50, 100]}
                slots={{
                  loadingOverlay: () => (
                    <TableSpinner
                      isLoading={true}
                      isEmpty={false}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noResultsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noRowsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                loading={_QueryContentTranslationRead?.loading}
                disableColumnMenu={false}
                sx={{
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size="3xl"
          title="Create Translation"
          isOpen={isContentTranslationOpen}
          onClose={onContentTranslationClose}
          isCentered={false}
        >
          <ContentTranslationUpsert
            isCreateOnly={true}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="3xl"
          title="Update Translation"
          isOpen={isContentTranslationUpdateOpen}
          onClose={onContentTranslationUpdateClose}
          isCentered={false}
        >
          <ContentTranslationUpsert
            isCreateOnly={false}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="md"
          title="Translation Delete"
          isOpen={isContentTranslationDeleteOpen}
          onClose={onContentTranslationDeleteClose}
        >
          <ContentTranslationDelete />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORT
 */
export default Index;
